import { useEffect, useRef, useState } from "react";
import { api, fmtError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

export default function SchoolLayoutAccessBlock({ admin = false }) {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [layouts, setLayouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);

  useEffect(() => {
    const request = ++requestId.current;
    setLoading(true); setError(""); setUserId(""); setSelected([]); setUsers([]);
    const path = admin ? "/admin/layout-access" : `/administration/layout-access${schoolId ? `?school_id=${encodeURIComponent(schoolId)}` : ""}`;
    api.get(path).then(({ data }) => {
      if (request !== requestId.current) return;
      const list = data.schools || [];
      setSchools(list);
      setLayouts((data.layouts || []).filter(l => !l.is_default));
      setUsers((data.users || []).filter(u => !u.student_id && u.account_type !== "student"));
      if (admin) setSelected(list.find(s => s.id === schoolId)?.delegable_layout_ids || []);
      if (!schoolId && list.length === 1) setSchoolId(list[0].id);
    }).catch(e => { if (request === requestId.current) setError(fmtError(e)); })
      .finally(() => { if (request === requestId.current) setLoading(false); });
    return () => { ++requestId.current; };
  }, [admin, schoolId]);

  const chooseUser = (id) => {
    setUserId(id);
    setSelected(users.find(u => u.id === id)?.allowed_layout_ids || []);
  };
  const save = async () => {
    if (!schoolId || (!admin && !userId) || loading || saving) return;
    setSaving(true);
    try {
      const path = admin ? `/admin/layout-school-access/${schoolId}` : `/administration/layout-access/${schoolId}/${userId}`;
      const { data } = await api.put(path, { allowed_layout_ids: selected });
      setSelected(data.allowed_layout_ids || []);
      if (!admin) setUsers(current => current.map(u => u.id === userId ? { ...u, allowed_layout_ids: data.allowed_layout_ids || [] } : u));
      toast.success(admin ? "Presets autorizados para a escola atualizados" : "Presets do professor atualizados");
    } catch (e) { toast.error(fmtError(e)); }
    finally { setSaving(false); }
  };
  return (
    <section className="border border-border bg-card mb-6" data-testid={admin ? "layout-school-policy" : "layout-school-delegation"}>
      <div className="border-b border-border px-6 py-4">
        <div className="label-eyebrow">{admin ? "admin geral · área privada" : "gestão da escola · professores"}</div>
        <h2 className="font-display text-2xl font-bold mt-1">{admin ? "Presets que cada escola pode liberar" : "Liberar presets aos professores"}</h2>
      </div>
      <div className="p-6 space-y-4">
        <p className="text-sm text-muted-foreground">{admin
          ? "Somente o administrador geral define os presets disponíveis para delegação. O gestor da escola poderá selecionar seus professores e liberar apenas estes presets. Retirar um preset aqui revoga as liberações delegadas, mas preserva concessões diretas do administrador."
          : "Escolha um professor da sua escola. Só podem ser liberados os presets autorizados pelo administrador geral. Alunos não recebem essa permissão. O cartão padrão permanece disponível aos docentes."}</p>
        <label className="block text-sm">Escola
          <select aria-label="Escola dos presets" value={schoolId} disabled={loading || saving} onChange={e => setSchoolId(e.target.value)} className="block border border-border p-2 w-full bg-card mt-1">
            <option value="">Selecione uma escola…</option>
            {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        {error && <p role="alert" className="text-destructive">{error}</p>}
        {loading ? <p role="status">Carregando presets…</p> : schoolId && !error && <>
          {!admin && <label className="block text-sm">Professor
            <select aria-label="Professor dos presets" value={userId} disabled={saving} onChange={e => chooseUser(e.target.value)} className="block border border-border p-2 w-full bg-card mt-1">
              <option value="">Selecione um professor…</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
            </select>
          </label>}
          {!layouts.length && <p className="text-sm">Nenhum preset adicional autorizado para esta escola.</p>}
          {(admin || userId) && <>
            <div className="grid md:grid-cols-2 gap-2">
              {layouts.map(l => <label key={l.id} className="border border-border p-3 flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={selected.includes(l.id)} disabled={saving} onChange={() => setSelected(current => current.includes(l.id) ? current.filter(id => id !== l.id) : [...current, l.id])} />
                <span><b>{l.name}</b><span className="block text-xs text-muted-foreground">{l.description}</span></span>
              </label>)}
            </div>
            <Button type="button" disabled={saving} onClick={save} data-testid="school-layout-save">{saving ? "Salvando…" : admin ? "Salvar autorização da escola" : "Salvar presets do professor"}</Button>
          </>}
        </>}
      </div>
    </section>
  );
}
